import { cn } from "@/lib/utils";

export interface SettingsSectionProps {
  // Omit for a body-only card — an identity summary, say, where the content
  // speaks for itself. With neither title nor description the header band is not
  // rendered at all, rather than left empty with a divider under it.
  title?: string;
  description?: string;
  children: React.ReactNode;
  // Actions for the section, in a footer band below a full-width divider — Save
  // and Discard for an editable section. Omit for a read-only one.
  footer?: React.ReactNode;
  className?: string;
  // Extra classes for the body, e.g. a grid instead of the default stack.
  bodyClassName?: string;
}

// A titled block on a settings page: a recessed header band carrying the title
// and a one-line description, the body, and an optional footer band for actions.
//
// Both bands are separated by dividers that span the full card width — the
// padding lives on the bands rather than the card, which is the same anatomy the
// date-range picker's card uses.
export function SettingsSection({
  title,
  description,
  children,
  footer,
  className,
  bodyClassName,
}: SettingsSectionProps): React.ReactElement {
  return (
    <section
      className={cn(
        "bg-card text-card-foreground overflow-hidden rounded-lg border",
        className
      )}
    >
      {(title || description) && (
        <header className="bg-muted/40 border-b px-6 py-4">
          {title && (
            <h2 className="text-base font-semibold tracking-tight">{title}</h2>
          )}
          {description && (
            <p className={cn("text-muted-foreground text-sm", title && "mt-1")}>
              {description}
            </p>
          )}
        </header>
      )}

      <div className={cn("space-y-4 px-6 py-5", bodyClassName)}>{children}</div>

      {footer && (
        <footer className="bg-muted/40 flex items-center justify-end gap-3 border-t px-6 py-4">
          {footer}
        </footer>
      )}
    </section>
  );
}
